/**
 * Public surface for view-once media. The chat composer / media viewer talks
 * to this; never directly to the store or the AES-GCM helpers.
 *
 * Mirrors iOS `ViewOnceCoordinator.swift` exactly. Pure logic + crypto +
 * IndexedDB. NO Solid UI. The `getDisplayMedia` permission watch is intentionally
 * OUT OF SCOPE here — a typed hook is provided so UI layers can wire it up.
 *
 * # Sender flow
 *   1. `coordinator.prepareForSend(plaintext, mimeType, blobUrl)`
 *      → returns `{ ciphertext, manifest }`. Caller uploads ciphertext to
 *        blobUrl, then embeds `manifest` in the encrypted message body.
 *   2. After delivery, the sender's outgoing copy can be consumed locally to
 *      preview once, OR wiped to forget it.
 *
 * # Receiver flow
 *   1. On message receipt, decode the manifest and call
 *      `coordinator.registerIncoming(manifest)`.
 *   2. UI renders a "tap to view" placeholder while state is `unviewed`.
 *   3. On tap, `coordinator.consume(mediaId, ciphertext)` returns the
 *      plaintext exactly once. Subsequent calls throw.
 *   4. UI dismissal calls `coordinator.finishConsume(mediaId)` which drops
 *      the manifest from IndexedDB for good.
 *
 * # Screen-capture hook
 * Web browsers cannot prevent screenshots or screen recording. The hook below
 * (`onScreenCapture`) is a typed stub that the UI layer wires to
 * `getDisplayMedia` change events (or `visibilitychange` heuristics). It does
 * NOT change view-once state — purely informational, same posture as iOS.
 *
 * The actual `getDisplayMedia` permission-watch wiring is NOT implemented here
 * (it is scope-creep for the pure-logic core). The UI layer should:
 *   1. Call `navigator.mediaDevices.getDisplayMedia(...)` or monitor
 *      `document.visibilityState` / a hypothetical `capturedDidChange`
 *      equivalent.
 *   2. On detection, call `coordinator.reportScreenCapture(mediaId)`.
 */

import { ViewOnceStore } from "./ViewOnceStore";
import {
  sealForUpload,
  openCiphertext,
  type ViewOnceManifest,
  type ViewOnceState,
} from "./ViewOnceMedia";

export type { ViewOnceManifest, ViewOnceState };

// ── Screen-capture policy hook (typed, intentionally unimplemented) ────────────

/**
 * Called by the UI layer when a screen-capture event is detected.
 *
 * Mirrors iOS `ViewOnceCoordinator.reportScreenCapture(mediaId:)`.
 *
 * Contract: this function MUST NOT change the view-once state. It is purely
 * informational — the recipient already saw the content; this notifies the
 * sender as a best-effort social signal.
 *
 * Web implementation note: unlike iOS (`UIScreen.capturedDidChangeNotification`
 * + `UIApplication.userDidTakeScreenshotNotification`), the web platform has
 * NO reliable screenshot-detection API. The closest mitigations are:
 *   - Monitor `getDisplayMedia` approval to detect active screen sharing.
 *   - Clear sensitive DOM on `visibilitychange === "hidden"`.
 * These are browser-heuristics, not guarantees. Document as a hard gap.
 *
 * The actual wiring of those heuristics belongs in the UI layer, not here.
 */
export type ScreenCaptureCallback = (mediaId: string) => void;

/** Custom event name for screen-capture notifications (mirrors iOS). */
export const SCREEN_CAPTURE_EVENT = "kaether:viewonce:screenCaptured" as const;

// ── ViewOnceCoordinator ───────────────────────────────────────────────────────

export class ViewOnceCoordinator {
  private readonly store: ViewOnceStore;
  private screenCaptureCallback: ScreenCaptureCallback | null = null;

  constructor(store?: ViewOnceStore) {
    this.store = store ?? new ViewOnceStore();
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  /**
   * Call once at app launch (before any chat view renders). Force-completes any
   * session that crashed mid-reveal so the key is never offered a second time.
   *
   * Mirrors iOS `ViewOnceCoordinator.bootRecovery()`.
   */
  async bootRecovery(now?: string): Promise<string[]> {
    return this.store.recoverInterruptedSessions(now);
  }

  // ── Send path ──────────────────────────────────────────────────────────────

  /**
   * Encrypt media for upload. Returns the combined ciphertext
   * (nonce|ciphertext|tag) and the manifest the caller embeds in the
   * encrypted message body.
   *
   * Registers the manifest as `.outgoing` in `.unviewed` state. If the upload
   * or send fails, call `wipeOutgoing(mediaId)` to drop the local state.
   */
  async prepareForSend(
    plaintext: Uint8Array,
    mimeType: string,
    blobUrl: string,
    mediaId?: string,
    now?: string,
  ): Promise<{ ciphertext: Uint8Array; manifest: ViewOnceManifest }> {
    const id = mediaId ?? crypto.randomUUID();
    const { ciphertext, manifest } = await sealForUpload(
      plaintext,
      id,
      blobUrl,
      mimeType,
    );
    await this.store.register(manifest, "outgoing", now);
    return { ciphertext, manifest };
  }

  /**
   * Sender forgets their outgoing copy without revealing it. The peer's copy
   * is unaffected — there is no way to reach into their device.
   */
  async wipeOutgoing(mediaId: string, now?: string): Promise<void> {
    await this.store.wipe(mediaId, now);
  }

  // ── Receive path ───────────────────────────────────────────────────────────

  /**
   * Register an incoming view-once manifest received via E2EE message.
   */
  async registerIncoming(
    manifest: ViewOnceManifest,
    now?: string,
  ): Promise<void> {
    await this.store.register(manifest, "incoming", now);
  }

  /**
   * Single-shot decrypt. Reveals the plaintext to the caller exactly once.
   * Subsequent calls (or calls after `wipe` / `markExpired`) throw.
   *
   * The state transition to `viewing` happens BEFORE this returns, so a crash
   * during decrypt cannot rewind to `unviewed`. Call `finishConsume` once the
   * UI has dismissed the reveal viewer.
   *
   * If decryption throws after `beginConsume` has already committed (e.g. the
   * ciphertext is corrupt), we still call `finishConsume` to burn the one shot
   * — same behaviour as iOS.
   */
  async consume(
    mediaId: string,
    ciphertext: Uint8Array,
    now?: string,
  ): Promise<Uint8Array> {
    const manifest = await this.store.beginConsume(mediaId, now);
    try {
      return await openCiphertext(ciphertext, manifest.key);
    } catch (err) {
      // Decryption failed with the right key — the ciphertext is corrupt or
      // from the wrong URL. We've already burned the one shot; force-finish so
      // the key is wiped even on the error path.
      await this.store.finishConsume(mediaId, now).catch(() => {
        // finishConsume can fail if mediaId not found (shouldn't happen), but
        // we swallow to surface the original crypto error.
      });
      throw err;
    }
  }

  /**
   * UI calls this when the reveal viewer is dismissed. Drops the manifest from
   * IndexedDB; the key is no longer recoverable.
   */
  async finishConsume(mediaId: string, now?: string): Promise<void> {
    await this.store.finishConsume(mediaId, now);
  }

  /**
   * Server reported the ciphertext is gone (TTL hit, manual delete, etc).
   * Mark `expired` so the UI can show "this disappeared" instead of
   * indefinitely showing a tap-to-view placeholder.
   */
  async markExpired(mediaId: string, now?: string): Promise<void> {
    await this.store.markExpired(mediaId, now);
  }

  // ── UI bindings ────────────────────────────────────────────────────────────

  async state(mediaId: string): Promise<ViewOnceState | null> {
    return this.store.state(mediaId);
  }

  async isConsumable(mediaId: string): Promise<boolean> {
    return (await this.store.state(mediaId)) === "unviewed";
  }

  // ── Screen-capture hook ────────────────────────────────────────────────────

  /**
   * Register a callback to receive screen-capture notifications.
   *
   * The UI layer should install its `getDisplayMedia` / `visibilitychange`
   * observers, then call this to hook into them. The coordinator dispatches a
   * `kaether:viewonce:screenCaptured` CustomEvent on `window` (if available)
   * AND calls the registered callback.
   *
   * Best-effort only — see type comment on `ScreenCaptureCallback`.
   */
  setScreenCaptureCallback(cb: ScreenCaptureCallback | null): void {
    this.screenCaptureCallback = cb;
  }

  /**
   * Report a detected screen-capture event for `mediaId`.
   * Does NOT change view-once state. Fires the registered callback and
   * dispatches `kaether:viewonce:screenCaptured` on `window`.
   *
   * Mirrors iOS `ViewOnceCoordinator.reportScreenCapture(mediaId:)`.
   */
  reportScreenCapture(mediaId: string): void {
    this.screenCaptureCallback?.(mediaId);
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent(SCREEN_CAPTURE_EVENT, { detail: { mediaId } }),
      );
    }
  }
}

// Re-export store + errors for consumers that want direct access.
export { ViewOnceStore } from "./ViewOnceStore";
export {
  ViewOnceNotFoundError,
  ViewOnceWrongStateError,
  ViewOnceStoreError,
  ViewOnceCryptoError,
  sealForUpload,
  openCiphertext,
  generateKey,
  uint8ToBase64,
  base64ToUint8,
  VIEW_ONCE_MANIFEST_VERSION,
} from "./ViewOnceMedia";
